export type CockpitRunFilters = {
  status?: string;
  state?: string;
  stopReason?: string;
  approvalStatus?: "not_required" | "pending" | "approved" | "rejected";
  runId?: string;
  from?: string;
  to?: string;
  limit?: number;
};

export type CockpitAuditCheck = {
  id: string;
  description?: string;
  status?: string;
};

export type CockpitRunSummary = {
  id: string;
  workItemId?: string;
  workItemTitle?: string;
  status: string;
  stopReason?: string;
  stopReasonDetail?: string;
  registryArtifact?: string;
  environment?: string;
  dryRun?: boolean;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  summary?: {
    iterations?: number;
    success?: boolean;
    audit?: { status?: string; checks?: number | CockpitAuditCheck[] };
    judge?: {
      finalAction?: "continue" | "stop" | "escalate";
      finalReason?: string;
      riskTier?: "low" | "medium" | "high" | "critical";
      escalationTriggers?: string[];
      approvalRequired?: boolean;
      actionCounts?: { continue?: number; stop?: number; escalate?: number };
    };
    repairLoop?: {
      verdict?: "converged" | "needs_approval" | "failed" | "no_op";
      bounded?: boolean;
      iterationsUsed?: number;
      maxIterations?: number;
      patchesProposed?: number;
      patchesApplied?: number;
      guardrailViolationCount?: number;
      failureSignatures?: string[];
      approvalRequired?: boolean;
      convergenceScore?: number;
    };
  };
  metrics?: {
    latencyMs?: number;
    completenessRate?: number;
    errorRate?: number;
    generatedAt?: string;
    plannedCommandCount?: number;
    executedCommandCount?: number;
    failedCommandCount?: number;
    e2ePlannedCommandCount?: number;
    e2eExecutedCommandCount?: number;
    e2eFailedCommandCount?: number;
    e2eSuccessRate?: number | null;
    success?: boolean;
  };
  artifacts?: {
    consoleLog?: string;
    structuredLog?: string;
    runSummary?: string;
    metrics?: string;
    failureReport?: string;
    diffSummary?: string;
    evidencePackManifest?: string;
    evidenceContract?: string;
    policyReport?: string;
    approvalRequest?: string;
    approvalDecision?: string;
    auditLog?: string;
    auditIntegrity?: string;
    judgeReport?: string;
    repairLoopReport?: string;
    evaluationReport?: string;
  };
  auditIntegrity?: {
    status?: "PASS" | "NEEDS_ATTENTION";
    totalSignals?: number;
    reasonCodes?: string[];
    checkedAt?: string;
    hashChainVerified?: boolean;
  };
  approval?: {
    required: boolean;
    status: "not_required" | "pending" | "approved" | "rejected";
    riskTier?: "low" | "medium" | "high" | "critical";
    requiredRoles?: string[];
    policyTriggerRoles?: string[];
    requestedAt?: string;
    decidedAt?: string;
    slaHours: number;
    deadlineAt?: string;
    remainingMs?: number;
    overdueMs?: number;
    escalation: "none" | "watch" | "breached" | "escalated";
  };
  trace?: {
    runSpecHash?: string;
    configHash?: string | null;
    source?: string;
  };
};

export async function fetchCockpitRuns(projectId: string, filters: CockpitRunFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.state) params.set("state", filters.state);
  if (filters.stopReason) params.set("stopReason", filters.stopReason);
  if (filters.approvalStatus) params.set("approvalStatus", filters.approvalStatus);
  if (filters.runId) params.set("runId", filters.runId);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (typeof filters.limit === "number") params.set("limit", String(filters.limit));

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/cockpit/runs?${params.toString()}`,
  );
  if (!response.ok) {
    throw new Error(`Failed to load cockpit runs (${response.status})`);
  }
  return response.json() as Promise<{
    ok: boolean;
    items: CockpitRunSummary[];
    artifacts?: { runRegistry?: string; runList?: string };
  }>;
}
